/**
 * What the composition looks like at one point of the scene, as a value two poses can be compared
 * on.
 *
 * Read from the elements' ATTRIBUTES rather than from measured boxes: GSAP writes everything it
 * animates as an inline style (or, through AttrPlugin, as an attribute), so the attribute set is
 * what the timeline actually changed. Attributes the timeline never touches are constant and cancel
 * out of any comparison, which is what lets the whole set be captured without filtering it. Measured
 * boxes would need layout, which the composition has in the editor but not in a jsdom test, so a
 * rule built on them could not be exercised.
 *
 * A pose splits into a SHAPE and its NUMBERS, because "did anything change" and "did anything
 * visibly change" are different questions. A graphic can hold an element drifting a pixel a second
 * purely so the frame is never byte-identical; comparing poses exactly would call that motion and
 * report the scene as alive. Masking the numbers out of the shape lets a structural change (a
 * colour, a class, a path) count on sight, while a numeric one has to clear a threshold.
 *
 * A pose also carries each element's own text, because a counter ticking up or a line typing itself
 * rewrites `textContent` from an `onUpdate` and touches no attribute at all. That is real motion, it
 * survives a seek, and it reaches the video; read from attributes alone a stat scene would report as
 * frozen through the very beat that carries it.
 *
 * The blind spot is motion no seek can reproduce — a CSS `@keyframes` animation or a transition
 * writes nothing here and reads as a still frame. That is the right answer anyway: the rasterizer
 * renders by seeking a paused timeline, so such motion never reaches the video either.
 */

/**
 * The attributes whose numbers are values rather than identity, and are therefore masked out of the
 * shape and compared numerically. `d` and `points` are deliberately absent: their numbers are a
 * path, they can run to hundreds per element, and a morph between two of them is a change worth
 * reporting on sight rather than measuring.
 */
const NUMERIC_ATTRIBUTES = new Set([
    'style',
    'transform',
    'x',
    'y',
    'cx',
    'cy',
    'r',
    'rx',
    'ry',
    'width',
    'height',
    'opacity',
    'font-size',
    'stroke-width',
    'stroke-dasharray',
    'stroke-dashoffset',
    'offset-distance',
]);

const NUMBER_RE = /-?\d+(?:\.\d+)?(?:e[-+]?\d+)?/gi;

const TEXT_NODE = 3;

/**
 * Thresholds are RATES, not per-comparison deltas, so they do not move when the sampling cadence
 * does — and so they can be argued about in the units a viewer experiences.
 *
 * A normalized quantity (opacity, scale) crossing slower than this takes twenty seconds to travel
 * its full range: on screen that is a still frame.
 */
const NORMALIZED_RATE_PER_SECOND = 0.05;

/**
 * The same for pixels or degrees. At 1080p, six pixels a second crosses about half a percent of the
 * frame's height per second — the drift rate a graphic reaches for when it wants to look alive
 * without anything happening. Real motion is an order of magnitude faster: an element sliding 300px
 * into place over 0.7s travels at 400.
 */
const ABSOLUTE_RATE_PER_SECOND = 6;

/**
 * Values at or below this magnitude are read as normalized (opacity, scale), larger ones as pixels
 * or degrees. A heuristic — the numbers carry no units — but the two populations barely overlap in
 * practice, and the cost of misreading one is a threshold an order of magnitude out, not a wrong
 * answer about whether the element is on screen.
 */
const NORMALIZED_CEILING = 2;

export interface Pose {
    /** Per element, every attribute with the value-bearing numbers masked out. */
    shape: string[];
    /** Per element, those masked numbers, in the order they were written. */
    numbers: number[][];
}

/**
 * The element's own text, not its descendants': a leaf's change would otherwise also mark every
 * ancestor as changed, which says nothing extra and costs a string per level.
 */
function ownText(element: Element): string {
    let text = '';
    for (const node of Array.from(element.childNodes)) {
        if (node.nodeType === TEXT_NODE) {
            text += node.nodeValue ?? '';
        }
    }
    return text.replace(/\s+/g, ' ').trim();
}

function captureElement(element: Element): { shape: string; numbers: number[] } {
    const parts: string[] = [];
    const numbers: number[] = [];
    for (const attribute of Array.from(element.attributes)) {
        if (!NUMERIC_ATTRIBUTES.has(attribute.name)) {
            parts.push(`${attribute.name}=${attribute.value}`);
            continue;
        }
        for (const match of attribute.value.match(NUMBER_RE) ?? []) {
            const value = Number(match);
            if (Number.isFinite(value)) {
                numbers.push(value);
            }
        }
        parts.push(`${attribute.name}=${attribute.value.replace(NUMBER_RE, '#')}`);
    }
    // Sorted so attribute order can't matter, with the text appended after: text is not an
    // attribute and a `name=value` pair could otherwise collide with one.
    const text = ownText(element);
    return { shape: `${parts.sort().join('|')}${text ? `\u0000${text}` : ''}`, numbers };
}

/** `include` narrows the pose to the elements a question is about; every element when omitted. */
export function capturePose(document: Document, include?: (element: Element) => boolean): Pose {
    const body = document.body;
    if (!body) {
        return { shape: [], numbers: [] };
    }
    const elements = Array.from(body.querySelectorAll('*'))
        .filter((element) => !include || include(element))
        .map(captureElement);
    return {
        shape: elements.map((one) => one.shape),
        numbers: elements.map((one) => one.numbers),
    };
}

/**
 * Whether a channel travelled fast enough over `seconds` to be seen. A zero or negative span can't
 * carry a rate, so it is treated as any difference counting.
 */
export function movedPerceptibly(before: number, after: number, seconds: number): boolean {
    const delta = Math.abs(after - before);
    if (!(seconds > 0)) {
        return delta > 0;
    }
    const magnitude = Math.max(Math.abs(before), Math.abs(after));
    const rate =
        magnitude <= NORMALIZED_CEILING ? NORMALIZED_RATE_PER_SECOND : ABSOLUTE_RATE_PER_SECOND;
    return delta / seconds >= rate;
}

/**
 * Whether the frame visibly changed. A structural difference counts on sight; a numeric one has to
 * clear the threshold, so an element inching across the scene to keep the composition from looking
 * static does not read as the graphic being alive.
 */
export function posesPerceptiblyDiffer(before: Pose, after: Pose, seconds: number): boolean {
    if (before.shape.length !== after.shape.length) {
        return true;
    }
    if (before.shape.some((entry, index) => entry !== after.shape[index])) {
        return true;
    }
    return before.numbers.some((channels, element) => {
        const others = after.numbers[element];
        if (channels.length !== others.length) {
            return true;
        }
        return channels.some((value, channel) => movedPerceptibly(value, others[channel], seconds));
    });
}
